import React, { useState, useCallback } from 'react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import ResponsiveTable from './ResponsiveTable';

const SI = (slug, color) => `https://cdn.simpleicons.org/${slug}/${color}`;
const FAV = (domain) => `https://www.google.com/s2/favicons?sz=64&domain=${domain}`;

const PLATFORM_LOGOS = {
  // 국내
  '멜론': SI('melon', '00CD3C'),
  'MelOn': SI('melon', '00CD3C'),
  '벅스': FAV('bugs.co.kr'),
  '벅스(2016)': FAV('bugs.co.kr'),
  '벅스(2019)': FAV('bugs.co.kr'),
  '지니': FAV('genie.co.kr'),
  '플로': FAV('music-flo.com'),
  'FLO': FAV('music-flo.com'),
  '바이브': FAV('vibe.naver.com'),
  'wavve (구. pooq)': FAV('wavve.com'),
  // 글로벌 스트리밍
  'SPOTIFY': SI('spotify', '1DB954'),
  'APPLE MUSIC': SI('applemusic', 'FC3C44'),
  'YOUTUBE': SI('youtube', 'FF0000'),
  'YOUTUBE MUSIC': SI('youtubemusic', 'FF0000'),
  'AMAZON': SI('amazonmusic', 'FF9900'),
  'AMAZON MUSIC': SI('amazonmusic', 'FF9900'),
  'DEEZER': SI('deezer', 'FEAA2D'),
  'TIDAL': SI('tidal', '00FFFF'),
  'SOUNDCLOUD': SI('soundcloud', 'FF5500'),
  'PANDORA': SI('pandora', '3668FF'),
  'TIKTOK': SI('tiktok', '69C9D0'),
  'META': SI('meta', '0082FB'),
  'GOOGLE PLAY': SI('googleplay', '01875F'),
  'LINE MUSIC': SI('line', '00B900'),
  'KKBOX': SI('kkbox', '009B73'),
  'RAKUTEN': SI('rakutenmusic', 'BF0000'),
  'ANGHAMI': SI('anghami', 'A020F0'),
  'NETEASE CLOUD MUSIC': SI('neteasecloudmusic', 'C20C0C'),
  'YANDEX': SI('yandexmusic', 'FF0000'),
  'JIOSAAVN': SI('jiosaavn', '2BC5B4'),
  'AWA': FAV('awa.fm'),
  'MOOV': FAV('moov.hk'),
  'KUGOU MUSIC': FAV('kugou.com'),
  'KUWO MUSIC': FAV('kuwo.cn'),
  'TME (QQMUSIC, KUGOU, KUWO)': SI('tencentqq', '12B7F5'),
  'WANGYI': SI('neteasecloudmusic', 'C20C0C'),
  'AITING': FAV('aiting.com'),
};

const PLATFORM_BADGE_COLORS = {
  '멜론': '#00CD3C', 'MelOn': '#00CD3C',
  '벅스': '#FF5A00', '벅스(2016)': '#FF5A00', '벅스(2019)': '#FF5A00',
  '지니': '#0099FF', '플로': '#FF385C', 'FLO': '#FF385C', '바이브': '#00C362',
  'SPOTIFY': '#1DB954', 'APPLE MUSIC': '#FC3C44', 'YOUTUBE': '#FF0000',
  'YOUTUBE MUSIC': '#FF0000', 'AMAZON': '#FF9900', 'DEEZER': '#FEAA2D',
  'TIDAL': '#00FFFF', 'SOUNDCLOUD': '#FF5500', 'PANDORA': '#3668FF',
  'TIKTOK': '#69C9D0', 'GOOGLE PLAY': '#01875F', 'LINE MUSIC': '#00B900',
  'KKBOX': '#009B73', 'RAKUTEN': '#BF0000', 'ANGHAMI': '#A020F0',
};

function PlatformLogo({ name }) {
  const [failed, setFailed] = useState(false);
  const src = PLATFORM_LOGOS[name];
  const color = PLATFORM_BADGE_COLORS[name] || '#64748b';
  const initial = (name || '?')[0].toUpperCase();

  if (!src || failed) {
    return (
      <span
        className="inline-flex w-5 h-5 rounded-full items-center justify-center text-[9px] font-bold text-white shrink-0"
        style={{ backgroundColor: color }}>
        {initial}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      className="w-5 h-5 rounded object-contain shrink-0"
      onError={() => setFailed(true)}
    />
  );
}

const UNIFIED_BAR_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  xaxis: {
    categories,
    labels: { style: { fontSize: '12px', colors: '#94a3b8' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetY: -20,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  plotOptions: {
    bar: { dataLabels: { position: 'top' }, borderRadius: 4 },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: { height: 250, toolbar: { show: false } },
        yaxis: [{ labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } }],
        grid: { padding: { left: 24 } },
      },
    },
  ],
});

function ByPlatformsTable({ data, totalCount, currentPage, pageSize, me }) {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  const handlePlatformClick = useCallback(
    async (platformType) => {
      setSelectedPlatform(platformType);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/ByPlatform/getMonthlyPlatformAmounts`,
          {
            params: {
              platformType,
              clientId: me?.id,
              adminYn: me?.adminYn,
            },
          }
        );
        setMonthlyData(res.data);
      } catch (error) {
        console.error('차트 데이터 불러오기 실패:', error);
      }
    },
    [me]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: '순번',
        id: 'row',
        Cell: ({ row }) =>
          totalCount - ((currentPage - 1) * pageSize + row.index),
      },
      {
        Header: '플랫폼명',
        accessor: 'platformType',
        Cell: ({ value }) => (
          <span
            className="font-bold text-sky-600 cursor-pointer flex items-center gap-2"
            onClick={() => handlePlatformClick(value)}>
            <PlatformLogo name={value} />
            {value}
          </span>
        ),
      },
      {
        Header: '총 히트수',
        accessor: 'platformHitCount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '총 정산액',
        accessor: 'platformAmount',
        Cell: ({ value }) => (
          <span className="font-bold">{Number(value).toLocaleString()}</span>
        ),
      },
    ],
    [totalCount, currentPage, pageSize, handlePlatformClick]
  );

  return (
    <>
      <ResponsiveTable
        columns={columns}
        data={data}
        visibleOnMobile={[
          'row',
          'platformType',
          'platformHitCount',
          'platformAmount',
        ]}
      />

      {selectedPlatform && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedPlatform(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedPlatform(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedPlatform}" 최근 1년 정산 그래프
            </h2>
            <Chart
              type="bar"
              width="100%"
              height="300"
              series={[{ name: '월별 정산액', data: monthlyData.map((item) => item.amount) }]}
              options={UNIFIED_BAR_OPTIONS(
                monthlyData.map((item) => item.month),
                'platform-month-chart'
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ByPlatformsTable;
